import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { UnsavedChangesDialogComponent } from 'src/app/shared/dialogs/unsaved-changes/unsaved-changes-dialog.component';

/**
 * Implemented by any routed component that can be holding edits the user has not saved.
 *
 * Keeping the contract this small is what makes the guard reusable: the component decides what
 * "unsaved" means for it — one form's `dirty`, several forms', a wizard's step state — and the
 * guard only ever asks.
 */
export interface HasUnsavedChanges {
  /** Whether anything on the screen is holding edits that a navigation would discard. */
  hasUnsavedChanges(): boolean;
  /**
   * Names of the parts holding those edits, for the dialog to list — tab or section labels, in the
   * user's language. Optional: a screen with a single form has nothing useful to add.
   */
  unsavedChangesSections?(): string[];
}

/**
 * Confirms before leaving a screen that is holding unsaved edits.
 *
 * Attach it to any route whose component implements `HasUnsavedChanges`:
 *
 * ```ts
 * {
 *   path: 'fleets/:id',
 *   loadComponent: () => …,
 *   canDeactivate: [unsavedChangesGuard],
 * }
 * ```
 *
 * Returns a promise rather than an observable so a cancelled navigation resolves to `false`
 * exactly once — the router treats a never-completing observable as a hung navigation.
 *
 * Note this covers navigation *within the app*. Closing the tab or reloading is the browser's to
 * confirm, which takes a `beforeunload` listener on the component; this guard deliberately does
 * not register one, since a global listener outliving the screen is worse than the gap.
 */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = async (component) => {
  if (!component?.hasUnsavedChanges?.()) {
    return true;
  }

  const dialog = inject(MatDialog);
  const ref = dialog.open<UnsavedChangesDialogComponent, unknown, boolean>(UnsavedChangesDialogComponent, {
    width: '440px',
    // Every way out of the dialog has to mean something: dismissing it by backdrop or Escape
    // closes with `undefined`, which reads as "do not leave" below, so a stray click cannot
    // discard the edits.
    data: { sections: component.unsavedChangesSections?.() ?? [] },
  });

  return (await firstValueFrom(ref.afterClosed())) === true;
};
